import { Breadcrumbs } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconKey, IconPlus, IconRefresh } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SSHKeyResponseModel } from '@xver/apis/v1/ssh-keys/model';
import { Button, ConfirmActionModal } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import useBreadcrumbItems from '@xver/hooks/useBreadcrumbItems';
import usePageHeading from '@xver/hooks/usePageHeading';

import AddSSHKeyModal from './components/AddSSHKeyModal';
import SSHKeyListTable from './components/SSHKeyListTable';
import { useSSHKeyMutations } from './hooks/useSSHKeyMutations';
import { SSH_KEYS_QUERY_KEY, useSSHKeysQuery } from './hooks/useSSHKeysQuery';

export default function SSHKeysPage() {
    const { t } = useTranslation('sshKeys');
    const headingRef = usePageHeading(t('page.title'));
    const queryClient = useQueryClient();
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [pendingDelete, setPendingDelete] = useState<SSHKeyResponseModel | null>(null);

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.account'), href: '#' },
        { title: t('breadcrumb.sshKeys') },
    ]);

    const { data, isPending, isFetching } = useSSHKeysQuery();
    const { create, remove } = useSSHKeyMutations();

    const handleDelete = (key: SSHKeyResponseModel) => {
        setPendingDelete(key);
        openDelete();
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        remove.mutate(pendingDelete.id, {
            onSuccess: () => {
                closeDelete();
                setPendingDelete(null);
            },
        });
    };

    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: SSH_KEYS_QUERY_KEY });
    };

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin="xs" className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-row justify-between items-end">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                            {t('page.title')}
                        </h1>
                        <p className="w-auto max-w-135 text-base text-fg/68">
                            {t('page.description')}
                        </p>
                    </div>
                    <div className="flex flex-row justify-end items-end gap-2">
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconRefresh size={16} />}
                            onClick={handleRefresh}
                            isLoading={isFetching && !isPending}
                        >
                            {t('buttons.refresh')}
                        </Button>
                        <Button
                            size="sm"
                            leftIcon={<IconPlus size={16} />}
                            onClick={() => setModalOpen(true)}
                        >
                            {t('buttons.add')}
                        </Button>
                    </div>
                </div>
            </div>
            <div className="w-full">
                <DialogBox
                    label={t('table.title')}
                    description={t('table.description')}
                    classNames="p-0"
                >
                    <SSHKeyListTable rows={data} isLoading={isPending} onDelete={handleDelete} />
                </DialogBox>
            </div>
            <AddSSHKeyModal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={async (payload) => {
                    await create.mutateAsync(payload);
                    setModalOpen(false);
                }}
                isSubmitting={create.isPending}
            />

            <ConfirmActionModal
                opened={deleteOpened}
                onClose={() => {
                    if (remove.isPending) return;
                    closeDelete();
                    setPendingDelete(null);
                }}
                title={t('confirm.deleteTitle', 'Delete SSH key?')}
                message={t('confirm.deleteMessage', {
                    name: pendingDelete?.name ?? '',
                    defaultValue: '"{{name}}" will be removed from your account.',
                })}
                confirmLabel={t('confirm.deleteLabel', 'Delete key')}
                confirmVariant="danger"
                isLoading={remove.isPending}
                onConfirm={confirmDelete}
            />
            {/* Marker icon for tree clarity, not rendered */}
            <span className="hidden">
                <IconKey size={0} />
            </span>
        </div>
    );
}
